/**
 * Алгоритм совместимости по знакам зодиака
 * Основан на стихиях (Elements) и модальностях (Modalities)
 */

// Маппинг знаков на стихии
const ELEMENTS = {
  Aries: 'Fire',
  Leo: 'Fire',
  Sagittarius: 'Fire',
  Taurus: 'Earth',
  Virgo: 'Earth',
  Capricorn: 'Earth',
  Gemini: 'Air',
  Libra: 'Air',
  Aquarius: 'Air',
  Cancer: 'Water',
  Scorpio: 'Water',
  Pisces: 'Water'
}

// Маппинг знаков на модальности
const MODALITIES = {
  Aries: 'Cardinal',
  Cancer: 'Cardinal',
  Libra: 'Cardinal',
  Capricorn: 'Cardinal',
  Taurus: 'Fixed',
  Leo: 'Fixed',
  Scorpio: 'Fixed',
  Aquarius: 'Fixed',
  Gemini: 'Mutable',
  Virgo: 'Mutable',
  Sagittarius: 'Mutable',
  Pisces: 'Mutable'
}

/**
 * Получить стихию знака
 */
export function getElement(sign) {
  return ELEMENTS[sign] || null
}

/**
 * Получить модальность знака
 */
export function getModality(sign) {
  return MODALITIES[sign] || null
}

/**
 * Вычислить elementScore для пары знаков
 * Возвращает: +1, 0, или -1 (контрастные)
 */
export function getElementScore(signA, signB) {
  const elementA = getElement(signA)
  const elementB = getElement(signB)

  if (!elementA || !elementB) return 0

  // Same element → +1
  if (elementA === elementB) {
    return 1
  }

  // Complementary: Fire ↔ Air, Earth ↔ Water → +1
  if (
    (elementA === 'Fire' && elementB === 'Air') ||
    (elementA === 'Air' && elementB === 'Fire') ||
    (elementA === 'Earth' && elementB === 'Water') ||
    (elementA === 'Water' && elementB === 'Earth')
  ) {
    return 1
  }

  // Clashing: Fire ↔ Water, Air ↔ Earth → -1 (контрастные стихии)
  if (
    (elementA === 'Fire' && elementB === 'Water') ||
    (elementA === 'Water' && elementB === 'Fire') ||
    (elementA === 'Air' && elementB === 'Earth') ||
    (elementA === 'Earth' && elementB === 'Air')
  ) {
    return -1 // Контрастные стихии
  }

  // Остальные сочетания → 0
  return 0
}

/**
 * Вычислить modalityScore для пары знаков
 * Возвращает: +1 или 0
 */
export function getModalityScore(signA, signB) {
  const modalityA = getModality(signA)
  const modalityB = getModality(signB)

  if (!modalityA || !modalityB) return 0

  // Same modality → +1
  if (modalityA === modalityB) {
    return 1
  }

  // Разные модальности → 0 (для MVP)
  return 0
}

/**
 * Вычислить compatibilityPoints для пары знаков
 * Возвращает: +1, 0, или -1
 */
export function getCompatibilityPoints(signA, signB) {
  const elementScore = getElementScore(signA, signB)
  const modalityScore = getModalityScore(signA, signB)

  // raw = elementScore + 0.5 * modalityScore
  const raw = elementScore + 0.5 * modalityScore

  // Приведение к финальным очкам
  if (raw >= 1.0) {
    return 1 // +1
  } else if (raw <= -0.5) {
    return -1
  } else {
    return 0
  }
}

/**
 * Нормализовать compatibilityPoints в score 0-100
 */
export function normalizeScore(compatibilityPoints) {
  if (compatibilityPoints === 1) {
    return 80
  } else if (compatibilityPoints === 0) {
    return 60
  } else {
    return 40 // compatibilityPoints === -1
  }
}

/**
 * Добавить детерминированный jitter к score
 */
function addJitter(baseScore, userAId, userBId) {
  // Простая hash функция для детерминированного jitter
  const hash = (str) => {
    let hash = 0
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash // Convert to 32bit integer
    }
    return Math.abs(hash)
  }

  const combined = `${userAId}_${userBId}`
  const jitter = (hash(combined) % 15) - 7 // Диапазон -7...+7

  return Math.max(0, Math.min(100, baseScore + jitter))
}

/**
 * Получить финальный score с jitter
 */
export function getFinalScore(signA, signB, userAId, userBId) {
  const compatibilityPoints = getCompatibilityPoints(signA, signB)
  const baseScore = normalizeScore(compatibilityPoints)
  return addJitter(baseScore, userAId, userBId)
}

/**
 * Получить лейбл совместимости по score
 */
export function getCompatibilityLabel(score) {
  if (score >= 80) {
    return '✨ Instant connection'
  } else if (score >= 65) {
    return '💫 Strong match'
  } else if (score >= 45) {
    return '🌱 Good potential'
  } else {
    return '🔮 Unexpected match'
  }
}

/**
 * Получить цвет для лейбла совместимости (текст — не используется, везде чёрный)
 */
export function getCompatibilityColor(score) {
  return '#000000'
}

/**
 * Фон плашки совместимости по ТЗ
 */
export function getCompatibilityBgColor(score) {
  if (score >= 80) return '#FEF7D2' // ✨ Instant connection
  if (score >= 65) return '#FEE5D2' // 💫 Strong match
  if (score >= 45) return '#E5FED2' // 🌱 Good potential
  return '#ECD2FE' // 🔮 Unexpected match
}

// Ключи категорий для процентов в модалке совместимости (порядок: 3 в ряд)
export const COMPATIBILITY_CATEGORIES = [
  'love', 'sex', 'communication',
  'family', 'patience', 'trust'
]

/**
 * Детерминированный jitter для категории (диапазон ±6)
 */
function getCategoryJitter(userAId, userBId, categoryKey) {
  const hash = (str) => {
    let h = 0
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i)
      h = ((h << 5) - h) + char
      h = h & h
    }
    return Math.abs(h)
  }
  const seed = hash(`${userAId}_${userBId}_${categoryKey}`)
  return (seed % 13) - 6
}

/**
 * Бонус по стихиям для категории (пара знаков уже даёт elementScore; здесь — доп. бонус по наличию стихий)
 */
function getCategoryElementBonus(categoryKey, elementA, elementB, elementScore) {
  const hasFire = elementA === 'Fire' || elementB === 'Fire'
  const hasEarth = elementA === 'Earth' || elementB === 'Earth'
  const hasAir = elementA === 'Air' || elementB === 'Air'
  const hasWater = elementA === 'Water' || elementB === 'Water'

  switch (categoryKey) {
    case 'love':
      return hasWater ? (elementScore >= 0 ? 6 : 2) : (elementScore === -1 ? -4 : 0)
    case 'sex':
      return hasFire ? (elementScore >= 0 ? 8 : 4) : 0
    case 'communication':
      return hasAir ? (elementScore >= 0 ? 6 : -2) : (elementScore === -1 ? -4 : 0)
    case 'trust':
      return elementScore === 1 ? 6 : (elementScore === -1 ? -5 : 2)
    case 'patience':
      return hasEarth ? (elementScore >= 0 ? 6 : 0) : (elementScore === -1 ? -3 : 0)
    case 'family':
      return hasEarth ? (elementScore >= 0 ? 8 : 2) : 0
    default:
      return 0
  }
}

/**
 * Бонус по модальности для категории (modalityScore 1 = одинаковый ритм)
 */
function getCategoryModalityBonus(categoryKey, modalityScore) {
  if (modalityScore !== 1) return 0
  const modalityBoost = {
    love: 5,
    sex: 3,
    communication: 4,
    trust: 8,
    patience: 10,
    family: 5
  }
  return modalityBoost[categoryKey] ?? 0
}

/**
 * Проценты совместимости по категориям для модалки (0–100).
 * Только для пользователей с подпиской.
 *
 * @param {string} signA - знак текущего пользователя
 * @param {string} signB - знак профиля
 * @param {string} userAId - id текущего пользователя
 * @param {string} userBId - id профиля
 * @returns {Object} объект с ключами COMPATIBILITY_CATEGORIES и значениями 0–100
 */
export function getCategoryScores(signA, signB, userAId, userBId) {
  const elementScore = getElementScore(signA, signB)
  const modalityScore = getModalityScore(signA, signB)
  const compatibilityPoints = getCompatibilityPoints(signA, signB)
  const baseScore = normalizeScore(compatibilityPoints)
  const elementA = getElement(signA)
  const elementB = getElement(signB)

  const result = {}
  for (const key of COMPATIBILITY_CATEGORIES) {
    const elementBonus = getCategoryElementBonus(key, elementA, elementB, elementScore)
    const modalityBonus = getCategoryModalityBonus(key, modalityScore)
    const jitter = getCategoryJitter(userAId, userBId, key)
    const value = baseScore + elementBonus + modalityBonus + jitter
    result[key] = Math.max(0, Math.min(100, Math.round(value)))
  }
  return result
}
